import { Eye, Pencil, UserMinus } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { CompanyLogo } from "@/components/people/company-logo"
import { PersonAvatar } from "@/components/people/person-avatar"
import { ProjectThumb } from "@/components/people/project-thumb"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { Button } from "@/components/ui/primitives"
import { SearchableCombobox, type SearchableComboboxOption } from "@/components/ui/searchable-combobox"
import { SelectDropdown } from "@/components/ui/select-dropdown"
import { useTranslation } from "@/hooks/use-translation"
import {
  createNoteShare,
  deleteNoteShare,
  fetchNoteShareTargets,
  type NoteDetail,
  type NoteShareAccess,
  type NoteShareRow,
  type NoteShareSearchTarget,
  type NoteShareTarget,
} from "@/lib/notes-api"

const SEARCH_DEBOUNCE_MS = 250

function isAbortError(error: unknown) {
  return error instanceof Error && error.name === "AbortError"
}

function shareValue(target: NoteShareTarget, id: number) {
  return `${target}:${id}`
}

function targetOption(
  row: NoteShareSearchTarget,
  t: (key: "shareGroupCompany" | "shareGroupProjects" | "shareGroupPeople") => string,
): SearchableComboboxOption {
  if (row.type === "Company") {
    return {
      value: shareValue("Company", row.id),
      label: row.name,
      group: t("shareGroupCompany"),
      leading: <CompanyLogo company={row} size={24} />,
    }
  }
  if (row.type === "Project") {
    return {
      value: shareValue("Project", row.id),
      label: row.name,
      group: t("shareGroupProjects"),
      leading: <ProjectThumb project={row} className="size-6 rounded-lg text-[10px]" />,
    }
  }
  return {
    value: shareValue("User", row.id),
    label: row.name,
    group: t("shareGroupPeople"),
    leading: (
      <PersonAvatar person={{ first_name: row.first_name, last_name: row.last_name, email: row.email }} size={24} />
    ),
  }
}

function parseShareValue(value: string): { target: NoteShareTarget; id: number } | null {
  const index = value.indexOf(":")
  if (index <= 0) return null
  const target = value.slice(0, index)
  const id = Number(value.slice(index + 1))
  if (target !== "Company" && target !== "Project" && target !== "User") return null
  if (!Number.isInteger(id) || id <= 0) return null
  return { target, id }
}

function ShareAvatar({ share }: { share: NoteShareRow }) {
  if (share.target_type === "User" && share.person) {
    return <PersonAvatar person={share.person} size={32} state={share.active ? "default" : "inactive"} />
  }
  if (share.target_type === "Company" && share.company) {
    return <CompanyLogo company={share.company} size={32} />
  }
  if (share.target_type === "Project" && share.project) {
    return <ProjectThumb project={share.project} className="size-8 rounded-lg text-xs" />
  }
  return <span className="size-8 shrink-0 rounded-lg bg-ui-surface-raised" aria-hidden />
}

export function NoteShareDialog({
  note,
  onNote,
  ensureNote,
}: {
  note: NoteDetail
  onNote: (note: NoteDetail) => void
  ensureNote: () => Promise<NoteDetail>
}) {
  const { t } = useTranslation("notes")
  const [access, setAccess] = useState<NoteShareAccess>("read")
  const [rows, setRows] = useState<NoteShareSearchTarget[]>([])
  const [nextOffset, setNextOffset] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState<string | null>(null)
  const [targetValue, setTargetValue] = useState("")
  const [chosen, setChosen] = useState<SearchableComboboxOption | null>(null)
  const generation = useRef(0)
  const loadingMore = useRef(false)

  useEffect(() => {
    if (query == null) return
    const gen = ++generation.current
    const controller = new AbortController()
    setRows([])
    setNextOffset(null)
    setLoading(true)
    const timer = window.setTimeout(() => {
      fetchNoteShareTargets({ q: query, offset: 0, signal: controller.signal })
        .then((page) => {
          if (gen !== generation.current) return
          setRows(page.targets)
          setNextOffset(page.next_offset)
        })
        .catch((error: unknown) => {
          if (isAbortError(error) || gen !== generation.current) return
          toast.error(error instanceof Error ? error.message : t("loadFailed"))
        })
        .finally(() => {
          if (gen === generation.current) setLoading(false)
        })
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [query, t])

  const options = useMemo(() => rows.map((row) => targetOption(row, t)), [rows, t])

  const loadMore = useCallback(() => {
    if (query == null || nextOffset == null || loading || loadingMore.current) return
    const gen = generation.current
    const offset = nextOffset
    loadingMore.current = true
    fetchNoteShareTargets({ q: query, offset })
      .then((page) => {
        if (gen !== generation.current) return
        setRows((prev) => {
          const seen = new Set(prev.map((row) => shareValue(row.type, row.id)))
          return [...prev, ...page.targets.filter((row) => !seen.has(shareValue(row.type, row.id)))]
        })
        setNextOffset(page.next_offset)
      })
      .catch((error: unknown) => {
        if (isAbortError(error) || gen !== generation.current) return
        toast.error(error instanceof Error ? error.message : t("loadFailed"))
      })
      .finally(() => {
        loadingMore.current = false
      })
  }, [loading, nextOffset, query, t])

  const handleQuery = useCallback((next: string) => {
    setQuery(next)
  }, [])

  const parsed = parseShareValue(targetValue)

  const add = async () => {
    if (!parsed) return
    try {
      const current = await ensureNote()
      onNote(await createNoteShare(current.id, { target_type: parsed.target, target_id: parsed.id, access }))
      setTargetValue("")
      setChosen(null)
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : t("loadFailed"))
    }
  }

  const removeShare = async (shareId: number) => {
    try {
      onNote(await deleteNoteShare(note.id, shareId))
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : t("loadFailed"))
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <SearchableCombobox
          className="min-w-0 flex-1"
          value={targetValue}
          onChange={(value) => {
            setTargetValue(value)
            setChosen(options.find((option) => option.value === value) ?? null)
          }}
          options={options}
          selectedOption={chosen}
          filterLocally={false}
          onQueryChange={handleQuery}
          onReachEnd={loadMore}
          loading={loading && options.length === 0}
          emptyLabel={t("nothingFound")}
          placeholder={t("shareSearch")}
          ariaLabel={t("shareSearch")}
          portalToBody
          size="sm"
        />
        <SelectDropdown
          value={access}
          onChange={(id) => setAccess(id as NoteShareAccess)}
          fitContent
          size="sm"
          ariaLabel={t("shareColumnAccess")}
          options={[
            { value: "read", label: t("shareRead"), icon: <Eye className="h-3.5 w-3.5" /> },
            { value: "edit", label: t("shareEdit"), icon: <Pencil className="h-3.5 w-3.5" /> },
          ]}
        />
        <Button type="button" size="sm" disabled={!parsed} onClick={() => void add()}>
          {t("shareAdd")}
        </Button>
      </div>
      {note.shares.length > 0 ? (
        <table className="w-full border-collapse text-left text-xs">
          <thead>
            <tr className="text-[10px] font-semibold tracking-wide text-ui-text-muted uppercase">
              <th className="pb-2 font-semibold">{t("shareColumnWho")}</th>
              <th className="pb-2 font-semibold">{t("shareColumnAccess")}</th>
              <th className="pb-2" />
            </tr>
          </thead>
          <tbody>
            {note.shares.map((share) => (
              <tr key={share.id} className="border-t border-ui-border">
                <td className="py-2 pr-3">
                  <span className="flex min-w-0 items-center gap-2">
                    <ShareAvatar share={share} />
                    <span className="min-w-0">
                      <span className="block truncate text-ui-text-primary">{share.label}</span>
                      <span className="block truncate text-ui-text-muted">
                        {share.target_type === "Company"
                          ? t("shareCompany")
                          : share.target_type === "Project"
                            ? t("shareProject")
                            : t("sharePerson")}
                        {share.active ? "" : ` · ${t("shareInactive")}`}
                      </span>
                    </span>
                  </span>
                </td>
                <td className="py-2 pr-3 whitespace-nowrap text-ui-text-secondary">
                  {share.access === "edit" ? t("shareEdit") : t("shareRead")}
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  <ActionHintTooltip label={t("shareRemove")}>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 shrink-0 p-0 text-ui-danger hover:bg-ui-danger/10 hover:text-ui-danger"
                      aria-label={t("shareRemove")}
                      onClick={() => void removeShare(share.id)}
                    >
                      <UserMinus className="h-4 w-4" />
                    </Button>
                  </ActionHintTooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  )
}
